// v3E.1 — Single medication detail page
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, Pill, ShieldAlert, Box, TrendingUp, Calendar, Loader2, Trash2,
    Check, X, AlarmClock, RefreshCw, ShoppingCart, Camera,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const STATUS_TONE = {
    pending: "bg-amber-100 text-amber-800",
    taken: "bg-emerald-100 text-emerald-700",
    delayed: "bg-emerald-50 text-emerald-700",
    skipped: "bg-rose-100 text-rose-700",
    missed: "bg-rose-100 text-rose-700",
    snoozed: "bg-blue-100 text-blue-700",
};

const TABS = ["Schedule", "Doses", "Refill", "Adherence", "Prescription"];

const MedicationDetailContent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("Schedule");
    const [reordering, setReordering] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data: d } = await api.get(`/reminder/medications/${id}`);
            setData(d);
        } catch (err) {
            if (err?.response?.status === 404) {
                toast.error("Medication not found");
                navigate("/reminders");
            }
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    const handleAction = async (doseId, action, extra = {}) => {
        try {
            setActionLoading(doseId);
            await api.post(`/reminder/doses/${doseId}/action`, { action, ...extra });
            toast.success("Updated");
            await load();
        } catch {
            toast.error("Could not update");
        } finally {
            setActionLoading(null);
        }
    };

    const oneClickReorder = async () => {
        try {
            setReordering(true);
            const { data: res } = await api.post(`/reminder/medications/${id}/one-click-reorder`);
            if (res.matched) {
                toast.success("Added to cart");
                navigate("/cart");
            } else {
                toast.info(res.message || "No catalog match — opening search");
                navigate(res.search_url || `/products?q=${encodeURIComponent(data?.medication?.medicine_name || "")}`);
            }
        } catch {
            toast.error("Could not reorder");
        } finally {
            setReordering(false);
        }
    };

    const togglePause = async () => {
        try {
            await api.patch(`/reminder/medications/${id}`, { active: !data.medication.active });
            toast.success(data.medication.active ? "Paused" : "Resumed");
            await load();
        } catch {
            toast.error("Could not toggle");
        }
    };

    const remove = async () => {
        if (!window.confirm("Delete this medication and its pending reminders?")) return;
        try {
            await api.delete(`/reminder/medications/${id}`);
            toast.success("Deleted");
            navigate("/reminders");
        } catch {
            toast.error("Delete failed");
        }
    };

    const uploadSelfie = async (doseId, file) => {
        if (!file) return;
        try {
            setActionLoading(doseId);
            const form = new FormData();
            form.append("file", file);
            await api.post(`/reminder/doses/${doseId}/upload-selfie-proof`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Proof uploaded — marked as taken");
            await load();
        } catch {
            toast.error("Upload failed");
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) return <div className="h-96 skeleton rounded-3xl" />;
    if (!data) return null;

    const m = data.medication;
    const schedules = data.schedules || [];
    const upcoming = data.upcoming_doses || [];
    const history = data.dose_history || [];
    const refill = data.refill_tracking;
    const adherence = data.adherence;

    return (
        <div className="space-y-5" data-testid="med-detail-page">
            {/* Header */}
            <header className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-1.5">
                            {m.is_critical && (
                                <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700" title={`Critical: ${m.critical_match}`}>
                                    <ShieldAlert className="h-2.5 w-2.5" /> critical
                                </span>
                            )}
                            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-700">{m.medicine_type}</span>
                            {!m.active && <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-700">paused</span>}
                        </div>
                        <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">
                            <Pill className="mr-1.5 inline h-5 w-5 text-brand-700" />
                            {m.medicine_name}
                        </h2>
                        <p className="mt-1 text-sm text-zinc-500">
                            {[m.strength, m.dosage_quantity].filter(Boolean).join(" · ")}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={oneClickReorder} disabled={reordering} className="btn-brand !py-2 !px-4 !text-sm" data-testid="med-reorder-btn">
                            {reordering ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShoppingCart className="h-3 w-3" />}
                            Reorder
                        </button>
                        <button onClick={togglePause} className="rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:border-brand-300" data-testid="med-pause-btn">
                            {m.active ? "Pause" : "Resume"}
                        </button>
                        <button onClick={remove} className="rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:border-rose-300" data-testid="med-delete-btn">
                            <Trash2 className="h-3 w-3" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Tabs */}
            <nav className="flex flex-wrap gap-1.5" data-testid="med-tabs">
                {TABS.map((t) => (
                    <button
                        key={t}
                        onClick={() => setActiveTab(t)}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                            activeTab === t ? "bg-brand-700 text-white" : "bg-white text-zinc-700 ring-1 ring-zinc-200 hover:ring-brand-300"
                        }`}
                        data-testid={`tab-${t.toLowerCase()}`}
                    >
                        {t}
                    </button>
                ))}
            </nav>

            {/* Content */}
            <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft">
                {activeTab === "Schedule" && (
                    <div data-testid="tab-content-schedule">
                        {schedules.length === 0 ? (
                            <p className="text-sm text-zinc-500">No active schedule.</p>
                        ) : (
                            schedules.map((s) => (
                                <div key={s.id} className="mb-3 rounded-2xl bg-zinc-50/40 p-4">
                                    <p className="text-sm font-semibold text-zinc-900">
                                        {s.schedule_type} · {s.relation_to_food?.replace("_", " ") || "n/a"}
                                        {s.critical && <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700"><ShieldAlert className="h-2.5 w-2.5" /> critical</span>}
                                    </p>
                                    <ul className="mt-2 space-y-1">
                                        {(s.time_slots || []).map((slot, i) => (
                                            <li key={i} className="flex items-center gap-2 text-sm">
                                                <AlarmClock className="h-3.5 w-3.5 text-brand-700" />
                                                <span className="font-mono">{slot.time}</span>
                                                <span className="text-zinc-500">— {slot.label}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {activeTab === "Doses" && (
                    <div data-testid="tab-content-doses">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Upcoming</h4>
                        <ul className="mt-2 space-y-2">
                            {upcoming.length === 0 && <li className="text-sm text-zinc-500">No upcoming doses.</li>}
                            {upcoming.slice(0, 10).map((d) => (
                                <li key={d.id} className="flex items-center justify-between rounded-xl bg-zinc-50/40 px-3 py-2 text-sm">
                                    <span>
                                        <span className={`mr-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONE[d.status]}`}>{d.status}</span>
                                        {new Date(d.scheduled_at).toLocaleString()} · {d.slot_label}
                                    </span>
                                    {d.status === "pending" && (
                                        <div className="flex gap-1">
                                            <button onClick={() => handleAction(d.id, "taken")} disabled={actionLoading === d.id} className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white hover:bg-emerald-600" data-testid={`dose-taken-${d.id}`}>
                                                <Check className="h-3 w-3" />
                                            </button>
                                            <label className="inline-flex cursor-pointer items-center rounded-full border border-zinc-200 px-2 py-0.5 text-[10px] text-zinc-600 hover:border-brand-300">
                                                <Camera className="h-3 w-3" />
                                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadSelfie(d.id, e.target.files?.[0])} />
                                            </label>
                                            <button onClick={() => handleAction(d.id, "skipped")} disabled={actionLoading === d.id} className="rounded-full border border-zinc-200 px-2 py-0.5 text-[10px] text-zinc-500 hover:border-rose-300 hover:text-rose-600" data-testid={`dose-skip-${d.id}`}>
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <h4 className="mt-5 text-xs font-semibold uppercase tracking-wider text-zinc-500">Recent history</h4>
                        <ul className="mt-2 space-y-2">
                            {history.length === 0 && <li className="text-sm text-zinc-500">No history yet.</li>}
                            {history.slice(0, 14).map((d) => (
                                <li key={d.id} className="flex items-center justify-between rounded-xl bg-white px-3 py-2 text-xs ring-1 ring-zinc-100">
                                    <span>{new Date(d.scheduled_at).toLocaleString()}</span>
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[d.status]}`}>{d.status}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {activeTab === "Refill" && (
                    <div data-testid="tab-content-refill">
                        {!refill ? (
                            <p className="text-sm text-zinc-500">Refill tracking not enabled. Edit medication to set it up.</p>
                        ) : (
                            <div className="grid grid-cols-2 gap-3">
                                <Stat label="Pills purchased" value={refill.quantity_purchased} />
                                <Stat label="Daily consumption" value={refill.daily_consumption} />
                                <Stat label="Remaining stock" value={refill.computed?.remaining_stock} />
                                <Stat label="Days remaining" value={refill.computed?.days_remaining ?? "—"} />
                                <Stat label="Refill by" value={refill.computed?.projected_refill_date || "—"} />
                                <Stat label="Alert level" value={refill.computed?.alert_level ? `${refill.computed.alert_level}d` : "—"} />
                            </div>
                        )}
                        <button onClick={oneClickReorder} disabled={reordering} className="mt-4 btn-brand !py-2 !px-4 !text-sm" data-testid="refill-reorder-btn">
                            {reordering ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShoppingCart className="h-3 w-3" />}
                            One-click reorder
                        </button>
                    </div>
                )}

                {activeTab === "Adherence" && adherence && (
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="tab-content-adherence">
                        <Stat label="24h" value={adherence.daily_compliance_pct != null ? `${adherence.daily_compliance_pct}%` : "—"} />
                        <Stat label="7d" value={adherence.weekly_compliance_pct != null ? `${adherence.weekly_compliance_pct}%` : "—"} />
                        <Stat label="30d" value={adherence.monthly_compliance_pct != null ? `${adherence.monthly_compliance_pct}%` : "—"} />
                        <Stat label="Lifetime" value={adherence.lifetime_compliance_pct != null ? `${adherence.lifetime_compliance_pct}%` : "—"} />
                        <Stat label="Current streak" value={`${adherence.current_streak_days}d`} />
                        <Stat label="Longest streak" value={`${adherence.longest_streak_days}d`} />
                        <Stat label="Total doses" value={adherence.lifetime_total} />
                    </div>
                )}

                {activeTab === "Prescription" && (
                    <div className="space-y-2 text-sm" data-testid="tab-content-prescription">
                        <Stat label="Doctor" value={m.prescription_details?.doctor_name || "—"} />
                        <Stat label="Hospital" value={m.prescription_details?.hospital_name || "—"} />
                        <Stat label="Start" value={m.prescription_details?.start_date || "—"} />
                        <Stat label="End" value={m.prescription_details?.end_date || "ongoing"} />
                        {m.prescription_id && (
                            <Link to="/prescriptions" className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline">
                                View prescription scan →
                            </Link>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const Stat = ({ label, value }) => (
    <div className="rounded-xl bg-zinc-50/40 p-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="mt-0.5 text-sm font-semibold text-zinc-900">{value ?? "—"}</p>
    </div>
);

const MedicationDetail = () => (
    <Layout>
        <section className="mx-auto max-w-4xl px-6 py-10 lg:px-12">
            <Link to="/reminders" className="mb-3 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700" data-testid="med-back-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to reminders
            </Link>
            <AuthGuard>
                <MedicationDetailContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default MedicationDetail;
